class CategorizedCollapsibleTabs {
    constructor() {
      this.init();
    }
  
    init() {
      this.categoryBoxes = document.querySelectorAll('.category-box');
      this.categoryContents = document.querySelectorAll('.collapsible-tabs__content');
      this.searchInput = document.getElementById('tab-search');
      this.searchClear = document.getElementById('search-clear');
      this.searchResultsInfo = document.getElementById('search-results-info');
      this.searchResultsCount = document.querySelector('.search-results-count');
      this.accordionItems = document.querySelectorAll('.accordion-item');
      this.wrapper = document.querySelector('.collapsible-tabs__wrapper');
      this.searchContainer = document.querySelector('.search-bar-container');
      
      // Document/Video specific elements
      this.checkboxes = document.querySelectorAll('.document-item__checkbox');
      this.downloadButtons = document.querySelectorAll('.download-selected-btn');
      this.singleDownloadButtons = document.querySelectorAll('[data-download-link], .document-item__action--download');
      
      if (this.categoryBoxes.length === 0) return;
      
      this.isSearchActive = false;
      this.originalActiveCategory = null;
      this.searchBarVisible = this.isSearchBarVisible();
      
      this.bindEvents();
      this.setInitialActiveCategory();
      this.updateAllDownloadButtonStates();
    }

    bindEvents() {
      // Category click events
      this.categoryBoxes.forEach(box => {
        box.addEventListener('click', (e) => {
          e.preventDefault();
          if (!this.isSearchActive) {
            this.handleCategoryClick(box);
          } else {
            // Prevent category switching during search
            e.stopPropagation();
          }
        });
      });

      // Search input events (only if search bar is visible)
      if (this.searchInput && this.searchBarVisible) {
        this.searchInput.addEventListener('input', (e) => {
          this.handleSearch(e.target.value);
        });

        this.searchInput.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            this.clearSearch();
          }
        });
      }

      // Clear search button (only if search bar is visible)
      if (this.searchClear && this.searchBarVisible) {
        this.searchClear.addEventListener('click', () => {
          this.clearSearch();
        });
      }

      // Handle window resize to update search bar visibility
      window.addEventListener('resize', () => {
        const newVisibility = this.isSearchBarVisible();
        if (newVisibility !== this.searchBarVisible) {
          this.searchBarVisible = newVisibility;
          if (!this.searchBarVisible && this.isSearchActive) {
            this.clearSearch();
          }
        }
      });

      // Checkbox change events
      this.checkboxes.forEach(checkbox => {
        checkbox.addEventListener('change', () => {
          this.updateDownloadButtonState(checkbox);
        });
      });

      // Download selected buttons
      this.downloadButtons.forEach(button => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          this.downloadSelectedDocuments(button);
        });
      });

      // Single document download buttons
      this.singleDownloadButtons.forEach(button => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.handleSingleDownload(button);
        });
      });
    }

    // Handle single document download
    handleSingleDownload(button) {
      const url = button.href || button.dataset.documentUrl;
      const title = button.dataset.documentTitle || button.getAttribute('aria-label') || 'document';
      
      if (!url) return;
      
      if (button.classList.contains('downloading')) return;
      
      // Add visual feedback
      button.classList.add('downloading');
      button.disabled = true;
      
      // Download the file
      this.forceDownloadFile(url, title)
        .then(() => {
          // Reset after download
          setTimeout(() => {
            button.classList.remove('downloading');
            button.disabled = false;
          }, 1000);
        })
        .catch((error) => {
          console.error('Download error:', error);
          button.classList.remove('downloading');
          button.disabled = false;
        });
    }

    handleCategoryClick(clickedBox) {
      const categoryId = clickedBox.dataset.category;
      
      // Remove active class from all boxes
      this.categoryBoxes.forEach(box => {
        box.classList.remove('active');
      });
      
      // Add active class to clicked box
      clickedBox.classList.add('active');
      
      // Hide all category contents
      this.categoryContents.forEach(content => {
        content.classList.remove('active');
      });
      
      // Show the selected category content
      const targetContent = document.querySelector(`[data-category-content="${categoryId}"]`);
      if (targetContent) {
        // Use a small delay to ensure smooth transition
        setTimeout(() => {
          targetContent.classList.add('active');
          this.updateDownloadButtonStateForCategory(categoryId);
        }, 100);
      }
    }

    handleSearch(searchTerm) {
      const term = searchTerm.trim().toLowerCase();
      
      if (term === '') {
        this.clearSearch();
        return;
      }

      this.activateSearchMode();
      
      let matchCount = 0;
      const searchTerms = term.split(' ').filter(t => t.length > 0);
      
      this.accordionItems.forEach(item => {
        const keywords = item.dataset.keywords || '';
        const title = item.dataset.tabTitle || '';
        const content = item.textContent.toLowerCase();
        
        const searchText = `${keywords} ${title} ${content}`;
        
        // Check if all search terms match
        const matches = searchTerms.every(searchTerm => 
          searchText.includes(searchTerm)
        );
        
        if (matches) {
          item.classList.add('search-match');
          matchCount++;
          this.highlightSearchTerms(item, searchTerms);
        } else {
          item.classList.remove('search-match');
          this.removeHighlights(item);
        }
      });

      this.updateSearchResults(matchCount, term);
      this.showSearchResults();
    }

    activateSearchMode() {
      if (!this.isSearchActive) {
        this.isSearchActive = true;
        this.originalActiveCategory = document.querySelector('.category-box.active');
        
        if (this.wrapper) {
          this.wrapper.classList.add('search-active');
        }
        
        // Hide all category-specific contents to avoid duplicates
        this.categoryContents.forEach(content => {
          content.classList.remove('active');
        });
        
        // Only show the "all" category content during search (which contains all tabs)
        const allCategoryContent = document.querySelector('[data-category-content="all"]');
        if (allCategoryContent) {
          allCategoryContent.classList.add('active');
        } else {
          // Fallback: if no "show all" section, show the first category content
          // but we'll hide duplicates through CSS
          if (this.categoryContents.length > 0) {
            this.categoryContents[0].classList.add('active');
          }
        }
      }
    }

    clearSearch() {
      this.isSearchActive = false;
      
      if (this.searchInput) {
        this.searchInput.value = '';
      }
      
      if (this.wrapper) {
        this.wrapper.classList.remove('search-active');
      }
      
      // Hide search UI elements
      if (this.searchClear) {
        this.searchClear.style.display = 'none';
      }
      
      if (this.searchResultsInfo) {
        this.searchResultsInfo.style.display = 'none';
      }
      
      // Remove search classes and highlights
      this.accordionItems.forEach(item => {
        item.classList.remove('search-match');
        this.removeHighlights(item);
      });
      
      // Restore original category state
      this.restoreOriginalCategoryState();
    }

    restoreOriginalCategoryState() {
      // Hide all contents first
      this.categoryContents.forEach(content => {
        content.classList.remove('active');
      });
      
      // Restore the originally active category
      if (this.originalActiveCategory) {
        const categoryId = this.originalActiveCategory.dataset.category;
        const targetContent = document.querySelector(`[data-category-content="${categoryId}"]`);
        if (targetContent) {
          setTimeout(() => {
            targetContent.classList.add('active');
          }, 100);
        }
      }
    }

    showSearchResults() {
      if (this.searchClear) {
        this.searchClear.style.display = 'flex';
      }
      
      if (this.searchResultsInfo) {
        this.searchResultsInfo.style.display = 'block';
      }
    }

    updateSearchResults(count, term) {
      if (this.searchResultsCount) {
        if (count === 0) {
          this.searchResultsCount.textContent = `No items found for "${term}"`;
        } else if (count === 1) {
          this.searchResultsCount.textContent = `1 item found for "${term}"`;
        } else {
          this.searchResultsCount.textContent = `${count} items found for "${term}"`;
        }
      }
    }

    highlightSearchTerms(item, searchTerms) {
      // Only highlight in the heading for better UX
      const heading = item.querySelector('.inline-richtext');
      if (heading && heading.textContent) {
        let text = heading.textContent;
        
        searchTerms.forEach(term => {
          if (term.length > 1) { // Only highlight terms longer than 1 character
            const regex = new RegExp(`(${this.escapeRegex(term)})`, 'gi');
            text = text.replace(regex, '<span class="search-highlight">$1</span>');
          }
        });
        
        heading.innerHTML = text;
      }
    }

    removeHighlights(item) {
      const highlights = item.querySelectorAll('.search-highlight');
      highlights.forEach(highlight => {
        const parent = highlight.parentNode;
        parent.replaceChild(document.createTextNode(highlight.textContent), highlight);
        parent.normalize();
      });
    }

    escapeRegex(string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    isSearchBarVisible() {
      if (!this.searchContainer) return false;
      
      const computedStyle = window.getComputedStyle(this.searchContainer);
      return computedStyle.display !== 'none';
    }

    setInitialActiveCategory() {
      // Set the first category as active if none is already active
      const activeBox = document.querySelector('.category-box.active');
      if (!activeBox && this.categoryBoxes.length > 0) {
        this.handleCategoryClick(this.categoryBoxes[0]);
      }
    }

    // Document download functionality
    updateDownloadButtonState(checkbox) {
      // Find the closest category content
      const categoryContent = checkbox.closest('.collapsible-tabs__content');
      if (!categoryContent) return;

      const downloadButton = categoryContent.querySelector('.download-selected-btn');
      if (!downloadButton) return;

      this.updateButtonStateForContainer(categoryContent, downloadButton);
    }

    updateDownloadButtonStateForCategory(categoryId) {
      const categoryContent = document.querySelector(`[data-category-content="${categoryId}"]`);
      if (!categoryContent) return;

      const downloadButton = categoryContent.querySelector('.download-selected-btn');
      if (!downloadButton) return;

      this.updateButtonStateForContainer(categoryContent, downloadButton);
    }

    updateButtonStateForContainer(container, downloadButton) {
      const checkboxes = container.querySelectorAll('.document-item__checkbox:checked');
      const count = checkboxes.length;
      
      const countElement = downloadButton.querySelector('.download-selected-count');
      if (countElement) {
        countElement.textContent = count;
      }

      downloadButton.disabled = count === 0;
    }

    updateAllDownloadButtonStates() {
      this.categoryContents.forEach(content => {
        const downloadButton = content.querySelector('.download-selected-btn');
        if (downloadButton) {
          this.updateButtonStateForContainer(content, downloadButton);
        }
      });
    }

    downloadSelectedDocuments(button) {
      const categoryContent = button.closest('.collapsible-tabs__content');
      if (!categoryContent) return;

      if (button.classList.contains('downloading')) return;

      const checkedCheckboxes = categoryContent.querySelectorAll('.document-item__checkbox:checked');
      
      if (checkedCheckboxes.length === 0) {
        this.showNoSelectionFeedback(button);
        return;
      }

      // Download each selected document
      const downloadUrls = [];
      checkedCheckboxes.forEach(checkbox => {
        const url = checkbox.dataset.documentUrl;
        if (url) {
          downloadUrls.push({
            url: url,
            title: checkbox.dataset.documentTitle || 'document'
          });
        }
      });

      // Show feedback
      this.showDownloadFeedback(button, downloadUrls.length);

      // Trigger downloads with a slight delay between each to prevent browser blocking
      downloadUrls.forEach((doc, index) => {
        setTimeout(() => {
          this.forceDownloadFile(doc.url, doc.title);
        }, index * 500);
      });
    }

    showNoSelectionFeedback(button) {
      button.style.opacity = '0.5';
      setTimeout(() => {
        button.style.opacity = '';
      }, 200);
    }

    // Force download a file by fetching it and creating a blob
    async forceDownloadFile(url, filename) {
      try {
        // Fetch the file
        const response = await fetch(url, {
          method: 'GET',
          mode: 'cors',
          credentials: 'same-origin'
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Get the blob from the response
        const blob = await response.blob();
        
        // Extract filename from URL if not provided
        if (!filename || filename === 'document') {
          const urlPath = new URL(url).pathname;
          filename = urlPath.split('/').pop() || 'document';
        }
        
        // Ensure filename has an extension
        if (!filename.includes('.')) {
          // Try to get extension from content-type
          const contentType = response.headers.get('content-type');
          if (contentType) {
            const extension = this.getExtensionFromMimeType(contentType);
            if (extension) {
              filename += extension;
            }
          }
        }

        // Create a blob URL and trigger download
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        link.style.display = 'none';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up the blob URL
        setTimeout(() => {
          window.URL.revokeObjectURL(blobUrl);
        }, 100);

      } catch (error) {
        console.error('Download failed:', error);
        // Fallback: try direct link download
        this.fallbackDownload(url, filename);
      }
    }

    // Fallback download method using anchor element
    fallbackDownload(url, filename) {
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'document';
      link.style.display = 'none';
      link.target = '_self';
      
      // Force the browser to treat it as a download
      link.setAttribute('download', filename || 'document');
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    // Get file extension from MIME type
    getExtensionFromMimeType(mimeType) {
      const mimeToExt = {
        'application/pdf': '.pdf',
        'application/msword': '.doc',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
        'application/vnd.ms-excel': '.xls',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
        'application/vnd.ms-powerpoint': '.ppt',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
        'application/zip': '.zip',
        'application/x-rar-compressed': '.rar',
        'image/jpeg': '.jpg',
        'image/png': '.png',
        'image/gif': '.gif',
        'image/webp': '.webp',
        'text/plain': '.txt',
        'text/csv': '.csv',
        'application/json': '.json'
      };
      
      return mimeToExt[mimeType.split(';')[0].trim()] || '';
    }

    showDownloadFeedback(button, count) {
      button.classList.add('downloading');
      button.disabled = true;
      
      // Reset after downloads complete (estimate based on file count)
      const resetDelay = Math.max(2000, count * 600 + 1000);
      setTimeout(() => {
        button.classList.remove('downloading');
        
        // Re-enable based on current selection
        const categoryContent = button.closest('.collapsible-tabs__content');
        if (categoryContent) {
          this.updateButtonStateForContainer(categoryContent, button);
        }
      }, resetDelay);
    }
  }
  
  // Initialize when DOM is loaded
  document.addEventListener('DOMContentLoaded', () => {
    new CategorizedCollapsibleTabs();
  });
  
  // Initialize on theme editor section load
  document.addEventListener('shopify:section:load', () => {
    new CategorizedCollapsibleTabs();
  });
